import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Clock, Sparkles } from "lucide-react";
import DataToolsPanel from "@/components/DataToolsPanel";

const FIELDS = [
  { key: "fallback_days", label: "Fallback interval (days)", hint: "Used for a customer's ordering rhythm until it has 3+ orders.", step: 1, min: 1 },
  { key: "reserved_prospects", label: "Reserved prospect slots", hint: "Always include at least this many new prospects in every day.", step: 1, min: 0 },
  { key: "slowed_multiplier", label: "Slowed threshold (× own average gap)", hint: "Flag a customer as 'slowed' when its current gap exceeds this × its own average.", step: 0.5, min: 1 },
  { key: "stopped_multiplier", label: "Stopped threshold (× own average gap)", hint: "Flag as 'stopped ordering' beyond this × its own average.", step: 0.5, min: 1 },
  { key: "min_reserved_score", label: "Minimum prospect score for My Day", hint: "Prospects below this 0–100 AI score are hidden from My Day, so weak leads and non-garages (tyre shops, car washes) never pad the list. Raise it to be stricter.", step: 5, min: 0 },
];

export default function SettingsPanel() {
  const [cfg, setCfg] = useState(null);
  const [gap, setGap] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/insights/order-gap").then((r) => setGap(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/settings/config", {
        fallback_days: Number(cfg.fallback_days),
        reserved_prospects: Number(cfg.reserved_prospects),
        slowed_multiplier: Number(cfg.slowed_multiplier),
        stopped_multiplier: Number(cfg.stopped_multiplier),
        min_reserved_score: Number(cfg.min_reserved_score),
      });
      setCfg(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#1E3A8A]" /></div>;

  return (
    <div data-testid="settings-panel" className="max-w-2xl">
      <div className="mb-4">
        <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[#1E3A8A]">Settings</h1>
        <p className="text-sm text-[#6B7280] mt-1">Tune how the day is ranked. Defaults are sensible — only touch them if something looks off.</p>
      </div>

      <div className="bg-[#EEF2FF] border border-[#C7D2FE] rounded-xl p-4 mb-5 flex items-start gap-3" data-testid="order-gap-metric">
        <Clock className="w-5 h-5 text-[#1E3A8A] mt-0.5" />
        <div>
          <div className="text-sm font-bold text-[#1E3A8A]">Average order gap across your customers</div>
          {gap && gap.avg_order_gap_days != null ? (
            <div className="text-sm text-[#374151]">Currently <b>{gap.avg_order_gap_days} days</b> (from {gap.customers_measured} of {gap.customers_total} customers with enough orders). This is a good guide for the fallback interval.</div>
          ) : (
            <div className="text-sm text-[#6B7280]">Not enough order history yet — needs customers with 3+ orders. Come back once you've logged more orders and this will tell you what the fallback should be.</div>
          )}
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 space-y-4">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block text-sm font-bold text-[#111827]">{f.label}</label>
            <p className="text-xs text-[#6B7280] mb-1.5">{f.hint}</p>
            <input type="number" step={f.step} min={f.min} value={cfg[f.key]} data-testid={`cfg-${f.key}`}
              onChange={(e) => setCfg({ ...cfg, [f.key]: e.target.value })}
              className="w-40 h-12 px-3 rounded-lg border border-[#D1D5DB] focus:outline-none focus:border-[#1E3A8A]" />
          </div>
        ))}
        <button onClick={save} disabled={saving} data-testid="save-settings"
          className="h-12 px-5 rounded-lg bg-[#1E3A8A] text-white font-bold flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save settings
        </button>
      </div>

      <div className="mt-4 text-xs text-[#9CA3AF] flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5" /> Thresholds compare each customer against its own ordering rhythm, so a monthly buyer is never falsely flagged.
      </div>

      <DataToolsPanel />
    </div>
  );
}
